import React, { useCallback, useState } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  Handle,
  Position,
  MarkerType,
} from '@xyflow/react';
// CSS loaded via public index.html or webpack — import path varies by version
import { Button, Select, Typography, Tag, Space, Drawer, Card } from 'antd';
import {
  PlusOutlined, SaveOutlined, FullscreenOutlined, TeamOutlined,
  MailOutlined, FacebookOutlined, RocketOutlined, CheckCircleOutlined,
} from '@ant-design/icons';

const { Text, Title } = Typography;

// ── Custom node types ────────────────────────────────────────────────────────

const STEP_COLORS = {
  start:    { bg: '#001529', border: '#1890ff', text: '#fff' },
  cdp:      { bg: '#722ed1', border: '#9254de', text: '#fff' },
  edm:      { bg: '#1677ff', border: '#4096ff', text: '#fff' },
  social:   { bg: '#1877f2', border: '#4096ff', text: '#fff' },
  ai:       { bg: '#13c2c2', border: '#36cfc9', text: '#fff' },
  demo:     { bg: '#fa8c16', border: '#ffa940', text: '#fff' },
  crm:      { bg: '#52c41a', border: '#73d13d', text: '#fff' },
  closed:   { bg: '#f5222d', border: '#ff4d4f', text: '#fff' },
  decision: { bg: '#fadb14', border: '#ffc53d', text: '#000' },
  scraper:  { bg: '#e91e63', border: '#f06292', text: '#fff' },
  admin:    { bg: '#fa8c16', border: '#ffa940', text: '#fff' },
  public:   { bg: '#1677ff', border: '#4096ff', text: '#fff' },
  inbox:    { bg: '#722ed1', border: '#9254de', text: '#fff' },
  contract: { bg: '#13c2c2', border: '#36cfc9', text: '#fff' },
  tool:     { bg: '#8b5cf6', border: '#a78bfa', text: '#fff' },
  external: { bg: '#0891b2', border: '#22d3ee', text: '#fff' },
  default:  { bg: '#fff',    border: '#d9d9d9', text: '#000' },
};

function StepNode({ data }) {
  const type = data.type || 'default';
  const colors = STEP_COLORS[type] || STEP_COLORS.default;
  return (
    <div style={{
      background: colors.bg,
      border: `2px solid ${colors.border}`,
      borderRadius: 10,
      padding: '10px 16px',
      minWidth: 160,
      maxWidth: 220,
      color: colors.text,
      boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
      fontSize: 13,
      cursor: 'pointer',
    }}>
      <Handle type="target" position={Position.Top} style={{ background: colors.border }} />
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
        {data.icon && <span style={{ marginRight: 6 }}>{data.icon}</span>}
        {data.label}
      </div>
      {data.platform && (
        <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4 }}>
          📍 {data.platform}
        </div>
      )}
      {data.kpi && (
        <div style={{
          background: 'rgba(255,255,255,0.15)',
          borderRadius: 4, padding: '2px 6px',
          fontSize: 10, marginTop: 4,
        }}>
          🎯 {data.kpi}
        </div>
      )}
      {data.time && (
        <Tag style={{ marginTop: 4, fontSize: 10 }} color="default">{data.time}</Tag>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: colors.border }} />
    </div>
  );
}

function DecisionNode({ data }) {
  return (
    <div style={{
      background: STEP_COLORS.decision.bg,
      border: `2px solid ${STEP_COLORS.decision.border}`,
      borderRadius: 0,
      transform: 'rotate(45deg)',
      width: 100, height: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    }}>
      <Handle type="target" position={Position.Top} style={{ top: -8 }} />
      <div style={{ transform: 'rotate(-45deg)', textAlign: 'center', fontSize: 12, fontWeight: 700 }}>
        {data.label}
      </div>
      <Handle type="source" position={Position.Bottom} id="yes" style={{ bottom: -8 }} />
      <Handle type="source" position={Position.Right} id="no" style={{ right: -8 }} />
    </div>
  );
}

const nodeTypes = { step: StepNode, decision: DecisionNode };

// ── NetAI Sales Flow (default) ───────────────────────────────────────────────

const NETAI_NODES = [
  { id: '1', type: 'step', position: { x: 300, y: 0 },
    data: { label: '名單整備', icon: '🗂️', type: 'cdp',
            platform: 'CDP /cdp', kpi: '297 位有效 email', time: '30 分鐘' } },
  { id: '2', type: 'step', position: { x: 300, y: 130 },
    data: { label: 'EDM 暖身（第1波）', icon: '📧', type: 'edm',
            platform: 'Email Campaigns', kpi: '開信率 > 20%', time: '1 天' } },
  { id: '3', type: 'step', position: { x: 300, y: 260 },
    data: { label: 'FB 廣告曝光', icon: '📣', type: 'social',
            platform: 'FB Ad Creator', kpi: '觸及 > 5,000', time: '2 天' } },
  { id: '4', type: 'decision', position: { x: 325, y: 380 },
    data: { label: '有開信？' } },
  { id: '4a', type: 'step', position: { x: 550, y: 390 },
    data: { label: '未開信 → 再追蹤', icon: '🔄', type: 'default',
            platform: 'Email Campaigns', time: '7天後', kpi: '重發一次' } },
  { id: '5', type: 'step', position: { x: 300, y: 510 },
    data: { label: 'EDM 案例故事（第2波）', icon: '📖', type: 'edm',
            platform: 'Email Campaigns', kpi: 'Demo 點擊 > 5', time: '1 天' } },
  { id: '6', type: 'step', position: { x: 300, y: 640 },
    data: { label: 'AI 自動生成提案', icon: '🤖', type: 'ai',
            platform: 'AI Presentation', kpi: '10頁客製簡報', time: '30 分鐘' } },
  { id: '7', type: 'decision', position: { x: 325, y: 760 },
    data: { label: '接受 Demo？' } },
  { id: '7a', type: 'step', position: { x: 550, y: 770 },
    data: { label: 'AI Chatbot 持續跟進', icon: '💬', type: 'ai',
            platform: 'LINE Bot /chatbot', time: '持續', kpi: '自動回覆 60%' } },
  { id: '8', type: 'step', position: { x: 300, y: 890 },
    data: { label: 'Demo 展示', icon: '🎬', type: 'demo',
            platform: 'AI Presentation', kpi: 'Demo → 報價 30%', time: '1 hr' } },
  { id: '9', type: 'step', position: { x: 300, y: 1020 },
    data: { label: '報價 / 試用', icon: '💰', type: 'crm',
            platform: 'CDP 活動追蹤', kpi: '14天試用轉化 40%', time: '14 天' } },
  { id: '10', type: 'step', position: { x: 300, y: 1150 },
    data: { label: '✅ 成交', icon: '🏆', type: 'closed',
            platform: 'CDP 升級活動 → 核准', kpi: '目標：Infra 54家 20%', time: '' } },
];

const NETAI_EDGES = [
  { id: 'e1-2', source: '1', target: '2', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e2-3', source: '2', target: '3', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e3-4', source: '3', target: '4', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e4-5', source: '4', target: '5', sourceHandle: 'yes', label: '有開信 ✓', style: { stroke: '#52c41a' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e4-4a', source: '4', target: '4a', sourceHandle: 'no', label: '未開信', style: { stroke: '#ff4d4f', strokeDasharray: '5,5' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e4a-2', source: '4a', target: '2', style: { stroke: '#ff4d4f', strokeDasharray: '5,5' }, label: '重發', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e5-6', source: '5', target: '6', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e6-7', source: '6', target: '7', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e7-8', source: '7', target: '8', sourceHandle: 'yes', label: '接受 ✓', style: { stroke: '#52c41a' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e7-7a', source: '7', target: '7a', sourceHandle: 'no', label: '拒絕', style: { stroke: '#ff4d4f', strokeDasharray: '5,5' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e7a-5', source: '7a', target: '5', style: { stroke: '#13c2c2', strokeDasharray: '5,5' }, label: '2週後再推', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e8-9', source: '8', target: '9', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'e9-10', source: '9', target: '10', animated: true, style: { stroke: '#52c41a', strokeWidth: 3 }, markerEnd: { type: MarkerType.ArrowClosed } },
];

// ── JJ Bangkok 房仲流程 ──────────────────────────────────────────────

const JJ_NODES = [
  // Row 0: External Data Sources
  { id: 'jj-s1', type: 'step', position: { x: 0, y: 0 },
    data: { label: 'FazWaz', icon: '🌐', type: 'scraper', platform: 'fazwaz.com', time: 'fazwaz_service.py' } },
  { id: 'jj-s2', type: 'step', position: { x: 180, y: 0 },
    data: { label: 'Thailand-Prop.', icon: '🌐', type: 'scraper', platform: 'thailand-property.com' } },
  { id: 'jj-s3', type: 'step', position: { x: 380, y: 0 },
    data: { label: 'LifeProperty', icon: '🌐', type: 'scraper', platform: 'lifepropertyasia.com', time: 'lifeproperty_service.py' } },
  { id: 'jj-s4', type: 'step', position: { x: 560, y: 0 },
    data: { label: 'Livinginsider', icon: '🌐', type: 'scraper', platform: 'livinginsider.com' } },
  { id: 'jj-s5', type: 'step', position: { x: 740, y: 0 },
    data: { label: 'HomeJet', icon: '🌐', type: 'scraper', platform: 'homejet.io' } },

  // Row 1: House AI Dashboard (import → DB)
  { id: 'jj-imp', type: 'step', position: { x: 320, y: 130 },
    data: { label: 'House AI 管理後台', icon: '📊', type: 'admin', platform: '/house-ai', kpi: '匯入 → house_properties', time: 'Import 按鈕觸發' } },

  // Row 2: Public Search Pages
  { id: 'jj-p1', type: 'step', position: { x: 0, y: 260 },
    data: { label: '租屋搜尋', icon: '🏠', type: 'public', platform: '/house-ai-rental', kpi: 'Condo / House / Villa' } },
  { id: 'jj-p2', type: 'step', position: { x: 200, y: 260 },
    data: { label: '住宅搜尋', icon: '🏡', type: 'public', platform: '/home-search', kpi: '房屋買賣/租賃' } },
  { id: 'jj-p3', type: 'step', position: { x: 400, y: 260 },
    data: { label: '辦公室搜尋', icon: '🏢', type: 'public', platform: '/office-search', kpi: '共享/商務辦公室' } },
  { id: 'jj-p4', type: 'step', position: { x: 600, y: 260 },
    data: { label: '飯店搜尋', icon: '🏨', type: 'public', platform: '/jj-hotels', time: 'hotel_api' } },

  // Row 3: Inquiry channels → Inbox
  { id: 'jj-ch1', type: 'step', position: { x: 60, y: 390 },
    data: { label: 'LINE 詢盤', icon: '💬', type: 'external', platform: 'LINE Messaging API', time: 'Webhook' } },
  { id: 'jj-ch2', type: 'step', position: { x: 240, y: 390 },
    data: { label: 'WhatsApp 詢盤', icon: '💬', type: 'external', platform: 'Meta Cloud API', time: 'Webhook' } },
  { id: 'jj-ch3', type: 'step', position: { x: 420, y: 390 },
    data: { label: 'WeChat 詢盤', icon: '💬', type: 'external', platform: 'WeChat Official', time: 'Webhook' } },
  { id: 'jj-inbox', type: 'step', position: { x: 280, y: 520 },
    data: { label: 'JJ Inbox 收件匣', icon: '📥', type: 'inbox', platform: '/jj-inbox', kpi: '多管道統一管理 | AI 自動回覆' } },

  // Row 4: CRM + Properties + Dashboard
  { id: 'jj-crm', type: 'step', position: { x: 80, y: 660 },
    data: { label: 'JJ CRM Pipeline', icon: '📋', type: 'crm', platform: '/jj-crm', kpi: '階段追蹤 (New → Won)' } },
  { id: 'jj-prop', type: 'step', position: { x: 300, y: 660 },
    data: { label: 'JJ 房源管理', icon: '🏙️', type: 'crm', platform: '/jj-properties', kpi: '屋主 AI 電話 / 摘要' } },
  { id: 'jj-dash', type: 'step', position: { x: 520, y: 660 },
    data: { label: 'JJ 儀表板', icon: '📈', type: 'admin', platform: '/jj-dashboard', kpi: '數據總覽' } },

  // Row 5: Viewing → Negotiation
  { id: 'jj-view', type: 'step', position: { x: 80, y: 800 },
    data: { label: '看房預約', icon: '👁', type: 'contract', platform: '/jj-inbox', kpi: '三方預約 · 結果記錄' } },
  { id: 'jj-nego', type: 'step', position: { x: 300, y: 800 },
    data: { label: '協商與報價', icon: '💰', type: 'contract', platform: '/house-ai', kpi: 'AI 建議還價策略' } },

  // Row 6: Contract + Renewal
  { id: 'jj-ctr', type: 'step', position: { x: 80, y: 940 },
    data: { label: '合約管理', icon: '📝', type: 'contract', platform: '/house-ai', kpi: 'AI 生成 · 電子簽署' } },
  { id: 'jj-renew', type: 'step', position: { x: 300, y: 940 },
    data: { label: 'JJ 續約管理', icon: '🔄', type: 'tool', platform: '/jj-renewals', kpi: '60d/30d/14d 提醒', time: '搬入確認 · 文件上傳' } },

  // Row 7: Closed
  { id: 'jj-done', type: 'step', position: { x: 200, y: 1080 },
    data: { label: '成交 / 結案', icon: '✅', type: 'closed', platform: 'CRM Move-in', kpi: '售後服務 · 轉介' } },

  // Row 8: Auxiliary tools
  { id: 'jj-ad', type: 'step', position: { x: 0, y: 1220 },
    data: { label: '廣告貼文追蹤', icon: '📢', type: 'tool', platform: '/jj-inbox (Ad Tab)', kpi: 'UTM 歸因 · 轉換率' } },
  { id: 'jj-kw', type: 'step', position: { x: 200, y: 1220 },
    data: { label: '關鍵字管理', icon: '🔑', type: 'tool', platform: '/jj-keywords', kpi: '自動匹配房源' } },
  { id: 'jj-ai', type: 'step', position: { x: 400, y: 1220 },
    data: { label: 'AI 引擎', icon: '🤖', type: 'external', platform: 'DeepSeek/Claude/Gemini', time: 'sortcut.comboware.ai' } },
  { id: 'jj-ala', type: 'step', position: { x: 620, y: 1220 },
    data: { label: 'AlayaEss', icon: '🔗', type: 'external', platform: 'alayaess.comboware.tw', kpi: 'Knowledge Push', time: '每6h排程' } },
];

const JJ_EDGES = [
  // Scrapers → Import
  { id: 'jje-s1', source: 'jj-s1', target: 'jj-imp', style: { stroke: '#e91e63', strokeDasharray: '3,3' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-s2', source: 'jj-s2', target: 'jj-imp', style: { stroke: '#e91e63', strokeDasharray: '3,3' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-s3', source: 'jj-s3', target: 'jj-imp', style: { stroke: '#e91e63', strokeDasharray: '3,3' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-s4', source: 'jj-s4', target: 'jj-imp', style: { stroke: '#e91e63', strokeDasharray: '3,3' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-s5', source: 'jj-s5', target: 'jj-imp', style: { stroke: '#e91e63', strokeDasharray: '3,3' }, markerEnd: { type: MarkerType.ArrowClosed } },
  // Import → Search Pages
  { id: 'jje-p1', source: 'jj-imp', target: 'jj-p1', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-p2', source: 'jj-imp', target: 'jj-p2', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-p3', source: 'jj-imp', target: 'jj-p3', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-p4', source: 'jj-imp', target: 'jj-p4', animated: true, markerEnd: { type: MarkerType.ArrowClosed } },
  // Search → Channels
  { id: 'jje-p1c', source: 'jj-p1', target: 'jj-ch1', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-p2c', source: 'jj-p2', target: 'jj-ch2', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-p3c', source: 'jj-p3', target: 'jj-ch3', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-p4c', source: 'jj-p4', target: 'jj-ch1', markerEnd: { type: MarkerType.ArrowClosed } },
  // Channels → Inbox
  { id: 'jje-c1i', source: 'jj-ch1', target: 'jj-inbox', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-c2i', source: 'jj-ch2', target: 'jj-inbox', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-c3i', source: 'jj-ch3', target: 'jj-inbox', markerEnd: { type: MarkerType.ArrowClosed } },
  // Inbox → CRM/Prop/Dash
  { id: 'jje-ic', source: 'jj-inbox', target: 'jj-crm', style: { stroke: '#52c41a', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-ip', source: 'jj-inbox', target: 'jj-prop', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-id', source: 'jj-inbox', target: 'jj-dash', style: { stroke: '#fa8c16' }, markerEnd: { type: MarkerType.ArrowClosed } },
  // CRM/Prop → Viewing
  { id: 'jje-cv', source: 'jj-crm', target: 'jj-view', style: { stroke: '#52c41a', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-pv', source: 'jj-prop', target: 'jj-view', markerEnd: { type: MarkerType.ArrowClosed } },
  // View → Negotiation → Contract → Renewal → Closed
  { id: 'jje-vn', source: 'jj-view', target: 'jj-nego', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-nc', source: 'jj-nego', target: 'jj-ctr', style: { stroke: '#13c2c2', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-cr', source: 'jj-ctr', target: 'jj-renew', markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-cd', source: 'jj-ctr', target: 'jj-done', style: { stroke: '#f5222d', strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-rd', source: 'jj-renew', target: 'jj-done', style: { stroke: '#f5222d' }, markerEnd: { type: MarkerType.ArrowClosed } },
  // Aux tools
  { id: 'jje-ia', source: 'jj-inbox', target: 'jj-ad', style: { stroke: '#8b5cf6', strokeDasharray: '5,5' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-pk', source: 'jj-prop', target: 'jj-kw', style: { stroke: '#8b5cf6', strokeDasharray: '5,5' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-iai', source: 'jj-inbox', target: 'jj-ai', style: { stroke: '#0891b2', strokeDasharray: '5,5' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-aal', source: 'jj-ai', target: 'jj-ala', style: { stroke: '#0891b2', strokeDasharray: '3,3' }, markerEnd: { type: MarkerType.ArrowClosed } },
  { id: 'jje-ald', source: 'jj-ai', target: 'jj-dash', style: { stroke: '#0891b2', strokeDasharray: '5,5' }, markerEnd: { type: MarkerType.ArrowClosed } },
];

const FLOW_MAP = {
  netai: { nodes: NETAI_NODES, edges: NETAI_EDGES, title: 'NetAI 銷售流程圖', subtitle: 'CDP → EDM → FB 廣告 → AI → CRM 成交' },
  jj: { nodes: JJ_NODES, edges: JJ_EDGES, title: 'JJ Bangkok 房仲流程圖', subtitle: '外部房源爬蟲 → House AI → 公開搜尋 → CRM → 成交管理' },
};

// ── Main component ────────────────────────────────────────────────────────────

export default function SalesFlowPage() {
  const [flowKey, setFlowKey] = useState('netai');
  const flow = FLOW_MAP[flowKey] || FLOW_MAP.netai;
  const [nodes, setNodes, onNodesChange] = useNodesState(flow.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flow.edges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const switchFlow = (key) => {
    setFlowKey(key);
    const f = FLOW_MAP[key] || FLOW_MAP.netai;
    setNodes(f.nodes);
    setEdges(f.edges);
    setDrawerOpen(false);
  };

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true, markerEnd: { type: MarkerType.ArrowClosed } }, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setDrawerOpen(true);
  }, []);

  return (
    <div style={{ padding: '0', height: 'calc(100vh - 96px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '12px 20px', background: '#fff', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>{flow.title}</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>{flow.subtitle} · 點擊節點查看詳細說明 · 拖拽移動 · 滾輪縮放</Text>
        </div>
        <Space>
          <Select value={flowKey} onChange={switchFlow} style={{ width: 260 }}
            options={[
              { value: 'netai', label: 'NetAI 銷售流程' },
              { value: 'jj', label: 'JJ Bangkok 房仲流程' },
              { value: 'edm', label: 'EDM 行銷流程' },
            ]} />
          <Button icon={<SaveOutlined />} onClick={() => {
            const data = JSON.stringify({ nodes, edges }, null, 2);
            const blob = new Blob([data], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = 'sales-flow.json'; a.click();
          }}>匯出</Button>
        </Space>
      </div>

      {/* Flow canvas */}
      <div style={{ flex: 1 }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          defaultEdgeOptions={{ animated: false, markerEnd: { type: MarkerType.ArrowClosed } }}
        >
          <Controls />
          <MiniMap
            nodeColor={(node) => {
              const colors = STEP_COLORS[node.data?.type] || STEP_COLORS.default;
              return colors.bg;
            }}
            style={{ background: '#f0f2f5' }}
          />
          <Background variant="dots" gap={16} size={1} color="#d9d9d9" />

          {/* Legend */}
          <Panel position="top-left">
            <div style={{ background: 'rgba(255,255,255,0.95)', borderRadius: 8, padding: '10px 14px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', fontSize: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>圖例</Text>
              {[
                { color: '#722ed1', label: 'CDP 資料中台' },
                { color: '#1677ff', label: 'Email 行銷' },
                { color: '#1877f2', label: '社群廣告' },
                { color: '#13c2c2', label: 'AI 工具' },
                { color: '#fa8c16', label: 'Demo / 提案' },
                { color: '#52c41a', label: 'CRM 追蹤' },
                { color: '#f5222d', label: '成交' },
                { color: '#fadb14', label: '決策點' },
              ].concat(flowKey === 'jj' ? [
                { color: '#e91e63', label: '外部房源爬蟲' },
                { color: '#fa8c16', label: '管理後台' },
                { color: '#1677ff', label: '公開搜尋頁' },
                { color: '#722ed1', label: '詢盤收件匣' },
                { color: '#13c2c2', label: '看房/合約' },
                { color: '#8b5cf6', label: '輔助工具' },
                { color: '#0891b2', label: '外部整合' },
              ] : []).map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: item.color, flexShrink: 0 }} />
                  <Text style={{ fontSize: 11 }}>{item.label}</Text>
                </div>
              ))}
            </div>
          </Panel>
        </ReactFlow>
      </div>

      {/* Node detail drawer */}
      <Drawer
        title={selectedNode?.data?.label}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={320}
      >
        {selectedNode && (
          <div>
            <Card size="small" style={{ marginBottom: 12, borderLeft: `4px solid ${(STEP_COLORS[selectedNode.data?.type] || STEP_COLORS.default).border}` }}>
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                {selectedNode.data.platform && (
                  <div><Text type="secondary">平台功能：</Text><br /><Tag color="purple">{selectedNode.data.platform}</Tag></div>
                )}
                {selectedNode.data.kpi && (
                  <div><Text type="secondary">KPI 目標：</Text><br /><Tag color="green">{selectedNode.data.kpi}</Tag></div>
                )}
                {selectedNode.data.time && (
                  <div><Text type="secondary">預估時間：</Text><br /><Tag color="orange">{selectedNode.data.time}</Tag></div>
                )}
              </Space>
            </Card>
            <Text type="secondary" style={{ fontSize: 12 }}>
              拖拽節點可調整位置，點擊節點下方的「+」連接下一個步驟。
            </Text>
          </div>
        )}
      </Drawer>
    </div>
  );
}
